import React from "react";
import { X } from "lucide-react";
import type { Transaction, VelaReviewSuggestion } from "../types";
import { ReviewCard } from "./ReviewCard";

interface ReviewDrawerProps {
  open: boolean;
  onClose: () => void;
  transactions: Transaction[];
  suggestions: VelaReviewSuggestion[];
  onPrimary: (transactionId: string) => void;
  onSkip: (transactionId: string) => void;
}

export function ReviewDrawer({
  open,
  onClose,
  transactions,
  suggestions,
  onPrimary,
  onSkip,
}: ReviewDrawerProps) {
  if (!open) return null;

  const findSuggestion = (txId: string) =>
    suggestions.find((s) => s.transactionId === txId);

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 bg-ink/40 z-[999] animate-[rdFade_0.25s_ease-out]"
      />
      <aside className="fixed top-0 right-0 bottom-0 w-full md:w-[520px] max-w-full bg-white z-[1000] shadow-[-8px_0_32px_rgba(14,26,26,0.15)] flex flex-col animate-[rdSlide_0.3s_ease-out]">
        <div className="px-5 md:px-6 py-4 border-b border-lineSoft flex justify-between items-start flex-shrink-0">
          <div>
            <h2 className="text-base font-extrabold text-ink m-0 -tracking-[0.015em]">
              Needs review
            </h2>
            <div className="text-xs text-ink3 mt-0.5 font-medium">
              {transactions.length} transaction
              {transactions.length === 1 ? "" : "s"} · about 10 seconds each
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close review drawer"
            className="w-8 h-8 rounded-lg bg-transparent border-none cursor-pointer text-ink3 flex items-center justify-center hover:bg-lineSoft"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 md:px-6 py-2 pb-8">
          {transactions.length === 0 ? (
            <div className="py-16 px-5 text-center text-ink4 text-[13px] font-medium">
              Nothing to review right now. Vela will flag items as new
              transactions arrive.
            </div>
          ) : (
            transactions.map((tx) => (
              <ReviewCard
                key={tx.id}
                transaction={tx}
                suggestion={findSuggestion(tx.id)}
                onPrimary={onPrimary}
                onSkip={onSkip}
              />
            ))
          )}
        </div>

        <style>{`
          @keyframes rdFade { from { opacity: 0; } to { opacity: 1; } }
          @keyframes rdSlide { from { transform: translateX(100%); } to { transform: translateX(0); } }
        `}</style>
      </aside>
    </>
  );
}